import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ShieldX } from 'lucide-react';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginForm from './components/LoginForm';
import AppShell from './components/AppShell';

/**
 * createAdminApp - Bootstrap an admin portal application.
 *
 * @param {object} config
 * @param {string} config.appName - Display name (e.g. "Recipe Genie Admin")
 * @param {string} config.apiUrl - Base API URL (e.g. "https://api-recipegenie.ioriented.com/api")
 * @param {string} [config.brandColor] - Brand color name: orange, blue, green, pink, purple
 * @param {React.ReactNode} [config.logo] - Logo element for login + sidebar
 * @param {string} [config.tagline] - Tagline shown on login screen
 * @param {Array} config.navItems - Sidebar nav: [{ key, label, icon, component, admin? }]
 * @param {function} config.createApiService - (ApiBase) => apiServiceInstance
 * @param {string} [config.requireRole] - Required user role to access the app (e.g. "admin")
 * @param {string} [config.rootElementId] - DOM element ID to mount into (default: "root")
 */
export function createAdminApp(config) {
  const {
    appName,
    apiUrl,
    brandColor = 'blue',
    logo,
    tagline,
    navItems,
    createApiService,
    requireRole,
    checkAccess,
    googleClientId,
    rootElementId = 'root',
  } = config;

  // Create the API service instance
  const apiService = createApiService(apiUrl);

  const hasRequiredRole = (userData) => {
    if (!requireRole) return true;
    return userData?.role === requireRole;
  };

  function App() {
    const [user, setUser] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [accessRestricted, setAccessRestricted] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      checkAuth();

      const handleAuthFailed = () => {
        setUser(null);
        setAccessDenied(false);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
      };
      window.addEventListener('authenticationFailed', handleAuthFailed);
      return () => window.removeEventListener('authenticationFailed', handleAuthFailed);
    }, []);

    const finalizeAuth = async (userData) => {
      if (!hasRequiredRole(userData)) {
        await apiService.logout();
        setAccessDenied(true);
        return false;
      }
      if (checkAccess && !checkAccess(userData)) {
        setAccessRestricted(true);
        setUser(userData);
        return false;
      }
      setUser(userData);
      return true;
    };

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const userData = await apiService.getCurrentUser();
        await finalizeAuth(userData);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
      } finally {
        setLoading(false);
      }
    };

    const handleLogin = async (credentials) => {
      try {
        setAccessDenied(false);
        setAccessRestricted(false);
        await apiService.login(credentials.username, credentials.password);
        const userData = await apiService.getCurrentUser();
        if (!hasRequiredRole(userData)) {
          await apiService.logout();
          return { success: false, error: 'Access denied. Admin privileges required.' };
        }
        if (checkAccess && !checkAccess(userData)) {
          setAccessRestricted(true);
          setUser(userData);
          return { success: true };
        }
        setUser(userData);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message || 'Login failed' };
      }
    };

    const handleGoogleLogin = async (idToken) => {
      try {
        setAccessDenied(false);
        setAccessRestricted(false);
        await apiService.socialLogin('google', idToken);
        const userData = await apiService.getCurrentUser();
        if (!hasRequiredRole(userData)) {
          await apiService.logout();
          return { success: false, error: 'Access denied.' };
        }
        if (checkAccess && !checkAccess(userData)) {
          setAccessRestricted(true);
          setUser(userData);
          return { success: true };
        }
        setUser(userData);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message || 'Google sign-in failed' };
      }
    };

    const handleLogout = async () => {
      try {
        await apiService.logout();
      } catch (error) {
        console.error('Logout error:', error);
      }
      setUser(null);
      setAccessDenied(false);
      setAccessRestricted(false);
    };

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-600 dark:border-gray-300" />
        </div>
      );
    }

    if (accessRestricted) {
      return (
        <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {logo && <div className="flex justify-center mb-6">{logo}</div>}
            <ShieldX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Access Restricted</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Web access is not enabled for your account. Please contact support to request access.
            </p>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }

    if (!user) {
      return (
        <LoginForm
          onLogin={handleLogin}
          onGoogleLogin={googleClientId ? handleGoogleLogin : undefined}
          googleClientId={googleClientId}
          appName={appName}
          logo={logo}
          brandColor={brandColor}
          tagline={tagline}
        />
      );
    }

    return (
      <AppShell
        appName={appName}
        logo={logo}
        brandColor={brandColor}
        navItems={navItems}
        user={user}
        api={apiService}
        onLogout={handleLogout}
      />
    );
  }

  // Mount the app
  const container = document.getElementById(rootElementId);
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <ThemeProvider apiService={apiService}>
        <App />
      </ThemeProvider>
    </React.StrictMode>
  );
}

// Re-export framework pieces for advanced usage
export { default as ApiBase } from './services/ApiBase';
export { ThemeProvider, useTheme } from './contexts/ThemeContext';
export { default as LoginForm } from './components/LoginForm';
export { default as AppShell } from './components/AppShell';
